import {
  graphQLErrorCode,
  IntegrationProvider,
  useDisconnectIntegration,
  useIntegrationConnections,
  useStartIntegrationConnection,
} from '@gripp/shared-logic';
import {
  Button,
  Colors,
  CustomAlert,
  Spacing,
  tokens,
  useSlideLayout,
} from '@gripp/shared-ui';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { INTEGRATION_CALLBACK_PATH } from '../../../shared/logging/integrationCallbackPrivacy';
import { useIntegrationSettingsAccess } from './useIntegrationSettingsAccess';

const provider = IntegrationProvider.CaseIh;

export const SettingsIntegrations = () => {
  const access = useIntegrationSettingsAccess();
  const { t } = useTranslation();
  const { setColumnTitle } = useSlideLayout();
  useEffect(() => {
    setColumnTitle(t('preferences.settings.integrations'));
  }, [setColumnTitle, t]);

  if (access.isLoading) return <Loading />;
  if (!access.canManage || !access.workspaceId) {
    return <Text style={styles.message}>{t('integrations.unavailable')}</Text>;
  }
  return (
    <IntegrationPane
      key={`${access.accountId}:${access.workspaceId}`}
      workspaceId={access.workspaceId}
    />
  );
};

const Loading = () => {
  const { t } = useTranslation();
  return (
    <View style={styles.loadingContainer}>
      <ActivityIndicator
        accessibilityLabel={t('integrations.loading')}
        color={Colors.primary}
      />
      <Text style={styles.body}>{t('integrations.loading')}</Text>
    </View>
  );
};

const IntegrationPane = ({ workspaceId }: { workspaceId: string }) => {
  const { t, i18n } = useTranslation();
  const { connections, isPending, isFetching, isError, refetch } =
    useIntegrationConnections(workspaceId);
  const { mutateAsync: start, reset: resetStart } =
    useStartIntegrationConnection(workspaceId);
  const { mutateAsync: disconnect } = useDisconnectIntegration(workspaceId);
  const connection = connections.find((item) => item.provider === provider);
  const connected = connection?.connected === true;
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const inFlight = useRef(false);
  const mounted = useRef(false);
  const pendingTab = useRef<Window | null>(null);

  useEffect(() => {
    setFeedback(null);
    setConfirmVisible(false);
  }, [connected]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      pendingTab.current?.close();
      pendingTab.current = null;
    };
  }, []);

  useEffect(() => {
    const refresh = async () => {
      if (document.visibilityState === 'hidden' || inFlight.current) return;
      try {
        await refetch({ throwOnError: true });
      } catch {
        if (mounted.current) setFeedback('integrations.loadError');
      }
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [refetch]);

  const showError = (error: unknown) => {
    if (!mounted.current) return;
    setFeedback(
      graphQLErrorCode(error) === 'INTEGRATION_CONNECTION_BUSY'
        ? 'integrations.busyError'
        : graphQLErrorCode(error) === 'FORBIDDEN'
        ? 'integrations.permissionError'
        : 'integrations.actionError'
    );
  };

  const connect = async () => {
    if (inFlight.current || isFetching || isError || connected) return;
    inFlight.current = true;
    setBusy(true);
    setFeedback(null);
    try {
      // Open within the user gesture so popup blocking is detected before a
      // state is minted. Detach before any external page can access the opener.
      const tab = window.open('about:blank', '_blank');
      if (!tab) {
        setFeedback('integrations.popupBlocked');
        return;
      }
      pendingTab.current = tab;
      tab.opener = null;
      const { authorizeUrl } = await start({
        provider,
        redirectUri: `${window.location.origin}${INTEGRATION_CALLBACK_PATH}`,
      });
      if (!mounted.current) return;
      if (tab.closed) {
        setFeedback('integrations.tabClosed');
        return;
      }
      const destination = new URL(authorizeUrl);
      if (destination.protocol !== 'https:')
        throw new Error('Invalid consent destination');
      tab.location.replace(destination.href);
      pendingTab.current = null;
      setFeedback('integrations.consentOpened');
    } catch (error: unknown) {
      showError(error);
    } finally {
      pendingTab.current?.close();
      pendingTab.current = null;
      resetStart();
      inFlight.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  const confirmDisconnect = async () => {
    if (
      inFlight.current ||
      isFetching ||
      isError ||
      !connected ||
      !confirmVisible
    )
      return;
    inFlight.current = true;
    setBusy(true);
    setConfirmVisible(false);
    setFeedback(null);
    try {
      await disconnect({ provider });
      if (mounted.current) setFeedback('integrations.disconnectedNotice');
    } catch (error: unknown) {
      showError(error);
    } finally {
      inFlight.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  const retry = async () => {
    try {
      await refetch({ throwOnError: true });
    } catch {
      if (mounted.current) setFeedback('integrations.loadError');
    }
  };

  const dateLabel = (value: string | null | undefined) => {
    if (!value || Number.isNaN(Date.parse(value)))
      return t('integrations.notAvailable');
    return new Date(value).toLocaleString(i18n.language);
  };
  const connectedName =
    [connection?.connectedBy?.name?.first, connection?.connectedBy?.name?.last]
      .filter(Boolean)
      .join(' ') || t('integrations.notAvailable');

  if (isPending) return <Loading />;
  if (isError) {
    return (
      <View style={styles.contentContainer}>
        <Text accessibilityRole="alert" style={styles.body}>
          {t('integrations.loadError')}
        </Text>
        <Button
          onPress={retry}
          disabled={isFetching}
          loading={isFetching}
          state="active"
          size="large"
          style={styles.actionButton}
        >
          {t('integrations.retry')}
        </Button>
      </View>
    );
  }

  return (
    <View style={styles.container} testID="settings-integrations-pane">
      <ScrollView contentContainerStyle={styles.contentContainer}>
        <Text style={styles.title}>
          {t('preferences.settings.integrations')}
        </Text>
        <Text style={styles.description}>{t('integrations.description')}</Text>
        <View style={styles.provider} testID="integration-provider-CASE_IH">
          <Text style={styles.title}>{t('integrations.cnh')}</Text>
          <Text style={styles.label} testID="integration-connection-status">
            {t(
              connected ? 'integrations.connected' : 'integrations.notConnected'
            )}
          </Text>
          {connected && (
            <>
              <View style={styles.metadata}>
                <Text style={styles.description}>
                  {t('integrations.connectedBy')}
                </Text>
                <Text style={styles.body} numberOfLines={1}>
                  {connectedName}
                </Text>
              </View>
              <View style={styles.metadata}>
                <Text style={styles.description}>
                  {t('integrations.connectedAt')}
                </Text>
                <Text style={styles.body}>
                  {dateLabel(connection?.connectedAt)}
                </Text>
              </View>
              <View style={styles.metadata}>
                <Text style={styles.description}>
                  {t('integrations.lastValidatedAt')}
                </Text>
                <Text style={styles.body}>
                  {dateLabel(connection?.lastValidatedAt)}
                </Text>
              </View>
            </>
          )}
        </View>
        {feedback && (
          <Text accessibilityLiveRegion="polite" style={styles.body}>
            {t(feedback)}
          </Text>
        )}
      </ScrollView>
      <View style={styles.contentBottomBar}>
        <Button
          onPress={connected ? () => setConfirmVisible(true) : connect}
          accessibilityLabel={t(
            connected ? 'integrations.disconnect' : 'integrations.connect'
          )}
          disabled={busy || isFetching}
          loading={busy}
          state={busy || isFetching ? 'default' : 'active'}
          size="large"
          style={styles.actionButton}
          testID="integration-connection-action"
        >
          {t(connected ? 'integrations.disconnect' : 'integrations.connect')}
        </Button>
      </View>
      <CustomAlert
        visible={confirmVisible}
        hideAlert={() => setConfirmVisible(false)}
        action={confirmDisconnect}
        title={t('integrations.disconnectConfirm')}
        primaryButtonLabel={t('integrations.disconnect')}
        secondaryButtonLabel={t('topNav.customAlert.cancel')}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.md,
  },
  contentContainer: {
    paddingVertical: Spacing.basePadding.paddingVertical,
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    gap: tokens.spacing.lg,
  },
  title: {
    ...tokens.typography.title.smallEmphasised,
    color: Colors.role.textStrong,
  },
  label: {
    ...tokens.typography.label.mediumEmphasised,
    color: Colors.role.textStrong,
  },
  body: {
    ...tokens.typography.body.small,
    color: Colors.role.textStrong,
    flexShrink: 1,
  },
  description: {
    ...tokens.typography.body.small,
    color: Colors.role.textSecondary,
  },
  message: {
    ...tokens.typography.body.small,
    color: Colors.role.textStrong,
    padding: tokens.spacing.lg,
  },
  provider: {
    backgroundColor: Colors.role.surfaceHover,
    padding: tokens.spacing.lg,
    borderRadius: tokens.borderRadius.md,
    gap: tokens.spacing.md,
  },
  metadata: { gap: tokens.spacing.xs },
  contentBottomBar: {
    padding: Spacing.baseMargin.base,
    backgroundColor: Colors.role.surface,
  },
  actionButton: {
    borderRadius: tokens.borderRadius.sm,
    width: '100%',
    height: 48,
  },
});
